import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Space, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { ArtemisJobRun } from "@/common/model";
import { JobRunTriggerType } from "@/common/model";
import { DateField, IdField } from "../base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "../responsive-table";
import { TaskStatusBadge } from "../tasks/task-badges";

interface JobRunsTableProps
	extends Omit<ResponsiveTableProps<ArtemisJobRun>, "columns"> {}

const TRIGGER_LABEL: Record<string, string> = {
	[JobRunTriggerType.MANUAL]: "Manual",
	[JobRunTriggerType.SCHEDULED]: "Scheduled",
};

export function JobRunsTable({ loading, ...props }: JobRunsTableProps) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();

	const columns: ColumnDef<ArtemisJobRun>[] = [
		{
			id: "status",
			accessorFn: (row) => row.task?.status,
			header: "Status",
			size: 120,
			enableSorting: true,
			filterFn: "equals",
			cell: ({ row }) =>
				row.original.task ? (
					<TaskStatusBadge status={row.original.task.status} />
				) : (
					"-"
				),
		},
		{
			accessorKey: "triggerType",
			header: "Trigger",
			size: 130,
			cell: ({ row }) => TRIGGER_LABEL[row.original.triggerType],
		},
		{
			accessorKey: "firedAt",
			header: "Executed At",
			size: 200,
			cell: ({ row }) => (
				<Typography.Text ellipsis>
					<DateField value={row.original.firedAt as string} />
					<Typography.Text
						type="secondary"
						style={{ marginLeft: 8, fontSize: 12 }}
					>
						(
						{formatDistanceToNow(new Date(row.original.firedAt as string), {
							addSuffix: true,
						})}
						)
					</Typography.Text>
				</Typography.Text>
			),
		},
		{
			accessorKey: "completedAt",
			header: "Completed At",
			size: 200,
			cell: ({ row }) => (
				<DateField value={row.original.completedAt as string | undefined} />
			),
		},
		{
			accessorKey: "taskId",
			header: "Task",
			cell: ({ row }) => <IdField id={row.original.taskId as string} />,
		},
	];

	return (
		<ResponsiveTable<ArtemisJobRun>
			columns={columns}
			loading={loading}
			initialSorting={[{ id: "createdAt", desc: true }]}
			compactRender={(run) => (
				<article
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<Space vertical size={2}>
						<IdField id={run.id} />
						<span>
							Task: <IdField id={run.taskId as string} />
						</span>
						<span>
							<Typography.Text ellipsis type="secondary">
								Executed At: <DateField value={run.firedAt as string} />
							</Typography.Text>
						</span>
					</Space>
					<Space orientation="vertical" align="end" size={2}>
						{run.task ? <TaskStatusBadge status={run.task.status} /> : null}
					</Space>
				</article>
			)}
			onRowClick={(r) =>
				navigate({
					to: `/${orgSlug}/workspaces/${r.workspaceId as string}/tasks/${r.taskId as string}`,
				})
			}
			{...props}
		/>
	);
}
